import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

export default function EmailsAdmin() {
    const [logs, setLogs] = useState([]);
    const [filter, setFilter] = useState("");
    const [retrying, setRetrying] = useState("");

    const load = () =>
        api.get("/admin/emails", { params: { status: filter } })
            .then((r) => setLogs(r.data))
            .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const retry = async (id) => {
        setRetrying(id);
        try {
            const { data } = await api.post(`/admin/emails/${id}/retry`);
            if (data.status === "sent") {
                toast.success("Email resent successfully");
            } else {
                toast.error(`Retry failed: ${data.error}`);
            }
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setRetrying("");
        }
    };

    return (
        <div data-testid="admin-emails">
            <div className="flex items-center gap-3 mb-4">
                <p className="text-sm text-slate-500 flex-1">
                    Transactional emails sent by the shop (order updates, receipts, password resets). Failed sends can be retried.
                </p>
                <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-44 bg-white" data-testid="emails-filter"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="admin-emails-list">
                {logs.length === 0 && <p className="p-6 text-sm text-slate-400">No emails logged yet.</p>}
                {logs.map((e) => (
                    <div key={e.id} className="flex items-center gap-4 p-4 flex-wrap" data-testid={`email-row-${e.id}`}>
                        <div className="flex-1 min-w-56">
                            <p className="font-bold text-slate-900 text-sm truncate">{e.subject}</p>
                            <p className="text-xs text-slate-500">
                                to {e.to} · {e.template} · {new Date(e.created_at).toLocaleString("en-GB")} · attempts: {e.attempts}
                            </p>
                            {e.error && <p className="text-xs text-red-600 mt-1">{e.error}</p>}
                        </div>
                        {e.status === "sent" ? (
                            <span className="rounded-full bg-green-100 text-green-700 text-xs font-bold px-3 py-1" data-testid={`email-status-${e.id}`}>Sent</span>
                        ) : e.status === "failed" ? (
                            <span className="rounded-full bg-red-100 text-red-700 text-xs font-bold px-3 py-1" data-testid={`email-status-${e.id}`}>Failed</span>
                        ) : (
                            <span className="rounded-full bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1" data-testid={`email-status-${e.id}`}>Pending</span>
                        )}
                        {e.status === "failed" && (
                            <Button variant="outline" size="sm" className="rounded-full" disabled={retrying === e.id}
                                onClick={() => retry(e.id)} data-testid={`email-retry-${e.id}`}>
                                <RefreshCw className={`h-4 w-4 mr-1 ${retrying === e.id ? "animate-spin" : ""}`} />
                                Retry
                            </Button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
